import React from 'react'
import {
  Box,
  Button,
  IconButton,
  Radio,
  Alert,
  TextareaAutosize,
  Checkbox,
  FormControlLabel,
  Switch
} from '@mui/material'
import DeleteIcon from '../../../../../common/icons/deleteIcon'
import PlusIcon from '../../../../../common/icons/plusIcon'
import { Field, useField } from 'react-final-form'
import {
  DEFAULT_DARK_MESSAGE_TEXT_COLOR,
  DEFAULT_DARK_TITLE_TEXT_COLOR,
  DEFAULT_LIGHT_THEME_CARD_BACKGROUND
} from '../../../../../common/constants/themeConstants'

export default function QuizEditor({
  name,
  contextError,
  duplicateError,
  emptyError,
  commentError,
  handleContextChange,
  handleCommentChange,
  handleQuizTypeChange,
  handleAddOption,
  handleDeleteOption,
  handleOptionChange,
  handleOptionBlur,
  isCurrentThemeLight,
  handleCorrectAnswerChange,
  MAX_CONTEXT_LENGTH,
  MAX_COMMENT_LENGTH
}) {
  const alignmentField = useField(`${name}.alignment`)
  const alignment = alignmentField.input.value || 'LEFT'
  const styles = getStyles(isCurrentThemeLight)
  return (
    <Box sx={styles.container}>
      {contextError && (
        <Alert
          severity='error'
          sx={{
            ...styles.duplicateAlert,
            width: '400px',
            alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
          }}
        >
          {contextError}
        </Alert>
      )}
      <Field name={`${name}.context`}>
        {({ input }) => (
          <TextareaAutosize
            {...input}
            placeholder='Enter a card title or main message'
            style={{
              ...styles.titleInput,
              color: isCurrentThemeLight ? DEFAULT_DARK_TITLE_TEXT_COLOR : 'rgba(255, 255, 255, 1)',
              textAlign: alignment
            }}
            onChange={e => handleContextChange(input, e.target.value)}
            maxLength={MAX_CONTEXT_LENGTH}
          />
        )}
      </Field>

      <Field name={`${name}.answers`}>
        {({ input: answersInput }) => {
          const options = Array.isArray(answersInput.value)
            ? answersInput.value
            : [{ sequenceNumber: 1, text: '', correct: true }]

          const hasMultipleCorrect = options.filter(opt => opt.correct).length > 1

          return (
            <>
              <Field name={`${name}.isMultipleChoice`}>
                {({ input: multipleChoiceInput }) => {
                  if (hasMultipleCorrect && !multipleChoiceInput.value) {
                    setTimeout(() => multipleChoiceInput.onChange(true), 0)
                  }

                  const isMultiChoice = multipleChoiceInput.value || hasMultipleCorrect

                  return (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isMultiChoice}
                          onChange={e => {
                            const newValue = e.target.checked
                            multipleChoiceInput.onChange(newValue)
                            handleQuizTypeChange(options, answersInput, newValue)
                          }}
                        />
                      }
                      label='Multiple Choice Question'
                      sx={styles.multipleChoiceSwitch}
                    />
                  )
                }}
              </Field>

              {duplicateError && (
                <Alert
                  severity='error'
                  sx={{
                    ...styles.duplicateAlert,
                    width: '400px',
                    alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
                  }}
                >
                  {duplicateError.message}
                </Alert>
              )}

              {emptyError && !duplicateError && (
                <Alert
                  severity='error'
                  sx={{
                    ...styles.duplicateAlert,
                    width: '400px',
                    alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
                  }}
                >
                  {emptyError.message}
                </Alert>
              )}

              <Field name={`${name}.isMultipleChoice`}>
                {({ input: multipleChoiceInput }) => {
                  const isMultipleChoice = multipleChoiceInput.value || hasMultipleCorrect
                  const sortedOptions = [...options].sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0))

                  return (
                    <Box
                      sx={{
                        ...styles.optionsContainer,
                        alignItems:
                          alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {sortedOptions.map(option => (
                        <Box
                          key={option.sequenceNumber}
                          sx={{
                            ...styles.optionItem,
                            alignSelf:
                              alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
                          }}
                        >
                          {isMultipleChoice ? (
                            <Checkbox
                              checked={option.correct}
                              onChange={() =>
                                handleCorrectAnswerChange(
                                  options,
                                  answersInput.onChange,
                                  option.sequenceNumber,
                                  isMultipleChoice
                                )
                              }
                              color='primary'
                              sx={styles.optionRadio}
                            />
                          ) : (
                            <Radio
                              checked={option.correct}
                              onChange={() =>
                                handleCorrectAnswerChange(
                                  options,
                                  answersInput.onChange,
                                  option.sequenceNumber,
                                  isMultipleChoice
                                )
                              }
                              color='primary'
                              sx={styles.optionRadio}
                            />
                          )}

                          <TextareaAutosize
                            value={option.text}
                            onChange={e =>
                              handleOptionChange(options, answersInput.onChange, option.sequenceNumber, e.target.value)
                            }
                            onBlur={() => handleOptionBlur(options, answersInput.onChange, option.sequenceNumber)}
                            placeholder='Enter option text'
                            style={{
                              ...styles.optionInput,
                              ...(duplicateError && duplicateError.sequenceNumber === option.sequenceNumber
                                ? styles.errorInput
                                : {}),
                              ...(emptyError && emptyError.sequenceNumber === option.sequenceNumber
                                ? styles.errorInput
                                : {})
                            }}
                            autoFocus
                          />

                          <IconButton
                            size='small'
                            onClick={() => handleDeleteOption(options, answersInput.onChange, option.sequenceNumber)}
                            disabled={options.length <= 1}
                            color='error'
                          >
                            <DeleteIcon color={'rgba(118, 118, 125, 1)'} />
                          </IconButton>
                        </Box>
                      ))}

                      <Button
                        startIcon={<PlusIcon color={'rgba(2, 70, 248, 1)'} style={{ width: 20, height: 20 }} />}
                        onClick={() => handleAddOption(options, answersInput.onChange)}
                        sx={{
                          ...styles.addButton,
                          alignSelf:
                            alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
                        }}
                        disabled={!!emptyError || !!duplicateError}
                      >
                        Add option
                      </Button>
                    </Box>
                  )
                }}
              </Field>
            </>
          )
        }}
      </Field>

      {commentError && (
        <Alert
          severity='error'
          sx={{
            ...styles.duplicateAlert,
            width: '400px',
            alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
          }}
        >
          {commentError}
        </Alert>
      )}
      <Field name={`${name}.comment`}>
        {({ input }) => (
          <TextareaAutosize
            {...input}
            placeholder='Learners will see this comment after choosing any answer'
            style={{
              ...styles.questionInput,
              width: '400px',
              alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start',
              marginLeft: alignment === 'CENTER' ? 'auto' : alignment === 'RIGHT' ? 'auto' : '0',
              marginRight: alignment === 'CENTER' ? 'auto' : alignment === 'RIGHT' ? '0' : 'auto'
            }}
            onChange={e => handleCommentChange(input, e.target.value)}
            maxLength={MAX_COMMENT_LENGTH}
          />
        )}
      </Field>
    </Box>
  )
}

const getStyles = isCurrentThemeLight => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px'
  },
  titleInput: {
    width: '100%',
    fontSize: '42px',
    fontWeight: 500,
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontFamily: 'DM Sans',
    padding: 0,
    margin: 0,
    backgroundColor: 'transparent',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  duplicateAlert: {
    borderRadius: '8px',
    color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 1)',
    backgroundColor: 'rgba(244, 67, 54, 0.15)'
  },
  errorInput: {
    border: '1px solid rgba(244, 67, 54, 0.5)',
    borderRadius: '4px'
  },
  questionInput: {
    width: '384px',
    fontSize: '18px',
    fontWeight: 500,
    outline: 'none',
    resize: 'none',
    fontFamily: 'Satoshi',
    borderRadius: '12px',
    padding: '8px',
    backdropFilter: 'blur(100px)',
    background: 'rgba(255, 255, 255, 1)',
    border: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'
    },
    color: DEFAULT_DARK_MESSAGE_TEXT_COLOR,
    minHeight: '60px'
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  optionItem: {
    display: 'flex',
    width: '400px',
    alignItems: 'center',
    fontFamily: 'DM Sans, sans-serif',
    padding: '12px 16px',
    borderRadius: '12px',
    backdropFilter: 'blur(100px)',
    backgroundColor: isCurrentThemeLight ? DEFAULT_LIGHT_THEME_CARD_BACKGROUND : 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s ease',
    border: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'
    }
  },
  optionRadio: {
    color: 'rgba(208, 208, 216, 1)',
    padding: '4px',
    marginRight: '8px',
    '&.Mui-checked': {
      color: 'rgba(2, 70, 248, 1)'
    }
  },
  optionInput: {
    width: '100%',
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: 'Satoshi',
    border: 'none',
    outline: 'none',
    resize: 'none',
    backgroundColor: 'transparent',
    color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 1)',
    flex: 1
  },
  addButton: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '400px',
    padding: '12px 16px',
    borderRadius: '12px',
    backdropFilter: 'blur(100px)',
    backgroundColor: isCurrentThemeLight ? DEFAULT_LIGHT_THEME_CARD_BACKGROUND : 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s ease',
    border: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'
    },
    '& .MuiButton-startIcon': {
      marginRight: '8px'
    },
    color: 'rgba(2, 70, 248, 1)',
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 700,
    fontSize: '16px',
    textTransform: 'none',
    marginTop: '8px',
    alignSelf: 'flex-start'
  },
  multipleChoiceSwitch: {
    color: isCurrentThemeLight ? DEFAULT_DARK_TITLE_TEXT_COLOR : 'rgba(255, 255, 255, 0.9)',
    fontFamily: 'DM Sans, sans-serif'
  }
})
